import React, { useEffect } from "react"
import PropTypes from "prop-types"
import { Form, InputNumber, Space } from "antd"
import { useTranslation } from "react-i18next"
import styles from "./BookingForm.module.css"

const ALL_GUEST_FIELDS = [
  { key: "adults", labelKey: "booking.labels.adults", allowedFlag: "adults_allowed", required: true },
  { key: "children", labelKey: "booking.labels.children", allowedFlag: "children_allowed", required: false },
  { key: "infants", labelKey: "booking.labels.infants", allowedFlag: "infants_allowed", required: false },
  { key: "passengers", labelKey: "booking.labels.passengers", allowedFlag: "passengers_allowed", required: false }
]

const GuestCountSection = ({ form, tripPrice, onChange, minGuests, maxGuests }) => {
  const { t } = useTranslation()

  // Default the Adults field to the experience's minimum guest count (fallback to 1).
  const defaultAdults = minGuests && minGuests > 0 ? minGuests : 1

  useEffect(() => {
    const mainCounts = form.getFieldValue("main") || {}
    onChange?.(mainCounts)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Sync the Adults default once minGuests resolves (it may arrive after first render).
  useEffect(() => {
    const adults = form.getFieldValue(["main", "adults"])
    if (adults == null || adults < defaultAdults) {
      form.setFieldValue(["main", "adults"], defaultAdults)
      handleChange()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultAdults])

  useEffect(() => {
    if (!tripPrice) return
    const updates = {}
    ALL_GUEST_FIELDS.forEach(({ key, allowedFlag, required }) => {
      if (!required && tripPrice[allowedFlag] === false) {
        updates[key] = 0
      }
    })
    if (Object.keys(updates).length > 0) {
      form.setFieldsValue({ main: { ...form.getFieldValue("main"), ...updates } })
      handleChange()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripPrice?.id])

  const handleChange = () => {
    setTimeout(() => {
      const mainCounts = form.getFieldValue("main") || {}
      onChange?.(mainCounts)
      // Re-validate the combined total field so the single min/max error updates as counts change.
      form.validateFields([["main", "adults"], "guestTotal"]).catch(() => {})
    }, 0)
  }

  // Single combined validator for the overall guest count against min/max limits.
  const validateGuestTotal = () => {
    const main = form.getFieldValue("main") || {}
    const total = Object.values(main).reduce((sum, v) => sum + (Number(v) || 0), 0)
    if (maxGuests != null && total > maxGuests) {
      return Promise.reject(new Error(t("booking.error_messages.max_guest_limit", { max: maxGuests })))
    }
    if (minGuests != null && total < minGuests) {
      return Promise.reject(new Error(t("booking.error_messages.min_guest_limit", { min: minGuests })))
    }
    return Promise.resolve()
  }

  return (
    <>
      <Space size="small" className={styles.inputGroup} wrap>
        {ALL_GUEST_FIELDS.map(({ key, labelKey, allowedFlag, required }) => {
          const disabled = tripPrice ? tripPrice[allowedFlag] === false : false
          return (
            <Form.Item
              key={key}
              label={t(labelKey)}
              name={["main", key]}
              initialValue={required ? defaultAdults : 0}
              rules={
                !disabled && required
                  ? [{ required: true, type: "number", min: 1, message: t("booking.error_messages.adults_required") }]
                  : []
              }
            >
              <InputNumber
                min={required ? 1 : 0}
                className={styles.inputNumber}
                disabled={disabled}
                onChange={handleChange}
              />
            </Form.Item>
          )
        })}
      </Space>
      <Form.Item name="guestTotal" rules={[{ validator: validateGuestTotal }]} />
    </>
  )
}

GuestCountSection.propTypes = {
  form: PropTypes.object.isRequired,
  tripPrice: PropTypes.shape({
    adults_allowed: PropTypes.bool,
    children_allowed: PropTypes.bool,
    infants_allowed: PropTypes.bool,
    passengers_allowed: PropTypes.bool
  }),
  onChange: PropTypes.func,
  minGuests: PropTypes.number,
  maxGuests: PropTypes.number
}

export default GuestCountSection
